import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { BusinessService } from '@/api';
import type { BusinessResponse } from '@/api/types';
import { handleAsyncThunkError } from './errorUtils';

// Business state interface
interface BusinessState {
  businesses: BusinessResponse[];
  isLoading: boolean;
  error: string | null;
  currentPage: number;
  pageSize: number;
  totalItems: number;
}

// Initial state
const initialState: BusinessState = {
  businesses: [],
  isLoading: false,
  error: null,
  currentPage: 1,
  pageSize: 20,
  totalItems: 0,
};

// Async thunks
export const fetchBusinesses = createAsyncThunk(
  'business/fetchBusinesses',
  async (params: {
    workspaceId: string;
    q?: string;
    page?: number;
    perPage?: number;
    includeUnavailable?: boolean;
  }, { rejectWithValue }) => {
    try {
      const response = await BusinessService.appsBusinessViewsListBusinesses(params);
      return {
        data: (response.data || []) as BusinessResponse[],
        meta: response.meta || { page: 1, per_page: 20, total: 0 }
      };
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '사업 목록 조회에 실패했습니다.');
    }
  }
);

export const createBusiness = createAsyncThunk(
  'business/createBusiness',
  async (businessData: {
    name: string;
    workspace_id: string;
    sort_key?: number;
    labels?: Array<{key: string; name: string}>;
    detail_fields?: Array<{key: string; name: string}>;
    extra_fields?: Array<{key: string; name: string}>;
  }, { rejectWithValue }) => {
    try {
      const response = await BusinessService.appsBusinessViewsCreateBusiness({
        requestBody: businessData
      });
      return response;
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '사업 생성에 실패했습니다.');
    }
  }
);

export const updateBusiness = createAsyncThunk(
  'business/updateBusiness',
  async (params: {
    businessId: string;
    data: {
      name?: string;
      sort_key?: number;
      available?: boolean;
      labels?: Array<{id?: string; key: string}>;
      detail_fields?: Array<{id?: string; key: string}>;
      extra_fields?: Array<{id?: string; key: string}>;
      ignored_reports?: string[];
    }
  }, { rejectWithValue }) => {
    try {
      const response = await BusinessService.appsBusinessViewsUpdateBusiness({
        businessId: params.businessId,
        requestBody: params.data
      });
      return response;
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '사업 수정에 실패했습니다.');
    }
  }
);

export const deleteBusiness = createAsyncThunk(
  'business/deleteBusiness',
  async (businessId: string, { rejectWithValue }) => {
    try {
      await BusinessService.appsBusinessViewsDeleteBusiness({ businessId });
      return businessId;
    } catch (error: unknown) {
      return handleAsyncThunkError(error, rejectWithValue, '사업 삭제에 실패했습니다.');
    }
  }
);

// Business slice
const businessSlice = createSlice({
  name: 'business',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearBusinesses: (state) => {
      state.businesses = [];
      state.error = null;
      state.currentPage = 1;
      state.totalItems = 0;
    },
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch businesses
      .addCase(fetchBusinesses.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchBusinesses.fulfilled, (state, action) => {
        state.isLoading = false;
        state.businesses = action.payload.data;
        state.currentPage = action.payload.meta.page;
        state.pageSize = action.payload.meta.per_page;
        state.totalItems = action.payload.meta.total;
        state.error = null;
      })
      .addCase(fetchBusinesses.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create business
      .addCase(createBusiness.fulfilled, (state, action) => {
        state.businesses.unshift(action.payload);
      })
      .addCase(createBusiness.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Update business
      .addCase(updateBusiness.fulfilled, (state, action) => {
        const index = state.businesses.findIndex(b => b.id === action.payload.id);
        if (index !== -1) {
          state.businesses[index] = action.payload;
        }
      })
      .addCase(updateBusiness.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      // Delete business
      .addCase(deleteBusiness.fulfilled, (state, action) => {
        state.businesses = state.businesses.filter(b => b.id !== action.payload);
      })
      .addCase(deleteBusiness.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearBusinesses, setCurrentPage, setPageSize } = businessSlice.actions;
export default businessSlice.reducer;